import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import WelcomeHero from 'flarum/forum/components/WelcomeHero';

// Mithril global.

/**
 * A video beside the welcome banner's words (Hero panel → Video).
 *
 * It reads the SITE's published theme, or the editor's draft while editing —
 * never a member's personal theme: a video is the site's content, not a look,
 * and a personal theme picked before the setting existed would hide it.
 *
 * By default it is a poster with a play button and nothing more is fetched
 * until someone presses it, so the forum's front page costs one image, not a
 * video, for every visitor. It then plays with its sound and controls. "Loop
 * silently" is for a short clip with no voice: browsers only autoplay muted.
 */
function video() {
  const editing = document.body.classList.contains('bespoke-editing');
  let doc = null;
  try {
    doc = editing && app.bespoke?.document ? app.bespoke.document : app.forum.attribute('bespoke.published');
  } catch (e) {
    return null;
  }
  const l = doc?.tokens?.layout || {};
  const src = String(l.heroVideo || '').trim();
  if (!src) return null;
  return {
    src,
    poster: String(l.heroVideoPoster || '').trim(),
    loop: !!l.heroVideoLoop,
    label: String(l.heroVideoLabel || '').trim()
  };
}
const clock = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const HeroVideo = {
  oninit() {
    this.playing = false;
    this.length = '';
  },
  view(vnode) {
    const v = vnode.attrs;
    if (v.loop) {
      return m('.BespokeHeroVideo', m('video', {
        src: v.src,
        poster: v.poster || undefined,
        autoplay: true,
        loop: true,
        playsinline: true,
        preload: 'auto',
        // Autoplay only starts muted, and `muted` must be the property, not just the attribute.
        oncreate: n => {
          n.dom.muted = true;
          if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) n.dom.pause();else n.dom.play?.()?.catch?.(() => {});
        }
      }));
    }
    return m('.BespokeHeroVideo', {
      className: this.playing ? 'is-playing' : ''
    }, [m('video', {
      src: v.src,
      poster: v.poster || undefined,
      preload: 'metadata',
      playsinline: true,
      controls: this.playing,
      onloadedmetadata: e => {
        const d = e.target.duration;
        if (isFinite(d) && d > 0) this.length = clock(d);
      }
    }), this.playing ? null : m('button.BespokeHeroVideo-play', {
      type: 'button',
      onclick: e => {
        const el = e.currentTarget.parentNode.querySelector('video');
        this.playing = true;
        el.muted = false;
        el.play?.()?.catch?.(() => {});
      }
    }, [m('i.fas.fa-play', {
      'aria-hidden': 'true'
    }), v.label || app.translator.trans('ernestdefoe-bespoke.forum.hero_video_watch'), this.length ? m('span.BespokeHeroVideo-length', this.length) : null])]);
  }
};
export default function heroVideo() {
  override(WelcomeHero.prototype, 'className', function (original) {
    return original() + (video() ? ' WelcomeHero--video' : '');
  });
  extend(WelcomeHero.prototype, 'bodyItems', function (items) {
    const v = video();
    // Only the words and the video share a row: anything else a theme or a
    // site's own CSS puts in the banner (a close button, a label) stays as it was.
    if (v && items.has('content')) items.setContent('content', m('.BespokeHeroSplit', [items.get('content'), m(HeroVideo, v)]));
  });
}